import React, { useEffect, useMemo, useState } from "react";

const FiscalYearSwitcher = ({ accountingClientId, yearsRange = 2 }) => {
  const storageKey = useMemo(() => {
    if (!accountingClientId) return null;
    return `activeFiscalYear:${accountingClientId}`;
  }, [accountingClientId]);

  const currentYear = new Date().getFullYear();
  const [activeYear, setActiveYear] = useState(currentYear);

  // Build options around current year, BUT also include saved year if outside range
  const options = useMemo(() => {
    const list = [];
    for (let i = -yearsRange; i <= yearsRange; i++) {
      const y = currentYear + i;
      list.push({ value: y, label: `${y}` });
    }

    // Ensure activeYear is always selectable
    if (activeYear && !list.some((o) => o.value === activeYear)) {
      list.push({ value: activeYear, label: `${activeYear}` });
      list.sort((a, b) => b.value - a.value); // optional nicer sorting
    }

    return list;
  }, [currentYear, yearsRange, activeYear]);

  // Load from localStorage per client
  useEffect(() => {
    if (!storageKey) return;

    const saved = parseInt(localStorage.getItem(storageKey), 10);
    const next = !isNaN(saved) ? saved : currentYear;

    setActiveYear(next);

    // if nothing saved yet, store default
    if (isNaN(saved)) localStorage.setItem(storageKey, String(next));
  }, [storageKey, currentYear]);

  const handleChange = (e) => {
    const y = parseInt(e.target.value, 10);
    if (isNaN(y)) return;

    setActiveYear(y);

    if (storageKey) {
      localStorage.setItem(storageKey, String(y));
    }

    window.dispatchEvent(new Event("fiscalYear:changed"));
  };

  if (!accountingClientId) return null;

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-gray-600">Fiscal Year</span>
      <select
        value={activeYear}
        onChange={handleChange}
        className="border border-gray-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default FiscalYearSwitcher;
